/** Reports API client (T73–T77). */
import { apiClient } from "./client";

/**
 * One period's money (#18843).
 *
 * Five figures, not one. "Doanh thu" used to mean `sum(total_vnd)` over invoices — which was both
 * missing exam fees and counting billed money as though it had been received. `billed` is what the
 * clinic charged; `netCollected` is what it actually has.
 */
export interface FinancialTotals {
  /** Doanh thu phát sinh — charged for valid, non-void business. */
  billed: number;
  /** Thực thu — money received, dated when it arrived. */
  collected: number;
  /** Đã hoàn — refunds. Whole-invoice only; the domain cannot express a partial refund. */
  refunded: number;
  /** Thực thu thuần = collected − refunded. */
  netCollected: number;
  /** Còn phải thu = billed − collected. */
  outstanding: number;
  /**
   * Hai kênh thu, tách riêng (#19344). `billed` là TỔNG của hai số này.
   *
   * Vì sao cần: `count` chỉ đếm hoá đơn, còn `billed` cộng cả phí khám — nên hai ô cạnh nhau trên
   * cùng một dòng đang nói về hai tập khác nhau. QA đối soát ngày 05/10 và thấy báo cáo
   * 2.140.000 ₫ so với một hoá đơn 2.100.000 ₫ trên màn Hoá đơn; 40.000 ₫ chênh là phí khám, và
   * không gì trên màn hình nói ra điều đó.
   */
  billedInvoice: number;
  billedExamFee: number;
}

export interface RevenueDailyRow extends FinancialTotals {
  date: string;
  /** Invoices ISSUED that day — a count of billing events, not of payments. */
  count: number;
}

export interface RevenueMonthlyRow extends FinancialTotals {
  month: string;
  count: number;
}

/**
 * Báo cáo bệnh nhân theo kỳ (#19846). "Lượt khám" = lịch hẹn đã HOÀN THÀNH (Q3).
 *
 * Nghĩa từng trường — đổi có chủ ý ở #19846, tên giữ nguyên:
 * - `newCount`: hồ sơ tạo trong kỳ (không gồm hồ sơ đã gộp).
 * - `visitedCount`: bệnh nhân có ≥ 1 lượt khám trong kỳ (hồ sơ gộp quy về hồ sơ gốc).
 * - `returningCount`: có lượt khám trong kỳ VÀ đã từng khám trước kỳ (trước: "có hoá đơn").
 * - `newVisitedCount`: bệnh nhân mới đã khám trong kỳ.
 * - `male` / `female` / `other`: trên bệnh nhân có lượt khám trong kỳ (trước: toàn bộ hồ sơ).
 * - `total`: mọi hồ sơ — TOÀN THỜI GIAN, không theo kỳ.
 */
export interface PatientsSummary {
  newCount: number;
  returningCount: number;
  total: number;
  male: number;
  female: number;
  other: number;
  visitedCount: number;
  newVisitedCount: number;
  /** `newCount` của kỳ trước — cùng số ngày, liền trước (#19864). */
  previousNewCount?: number;
}

/**
 * A ranking row. `billedVnd`, never "doanh thu": cash receipts are not allocated to invoice lines
 * in Addy HIS, so a per-service collected figure does not exist and must not be implied (#18843).
 */
export interface TopRow {
  id: string;
  name: string;
  count: number;
  billedVnd: number;
}

/**
 * Một bảng xếp hạng kèm tổng TOÀN KỲ (#19852). `grandTotalVnd` là của mọi mục trong kỳ chứ không
 * chỉ của top N, nên "% tích luỹ" có nghĩa và tính được dòng "Khác".
 */
export interface TopReport {
  items: TopRow[];
  grandTotalVnd: number;
  grandCount: number;
}

/** Một dòng Pareto: mục + % tích luỹ trên tổng TOÀN KỲ (#19852). */
export interface ParetoRow extends TopRow {
  cumulativePct: number;
}

/**
 * Pareto trên tổng toàn kỳ (#19852). Bản cũ chia cho tổng của chính top N nên dòng cuối luôn 100%.
 * `otherVnd` = tổng − top N: phần của mọi mục ngoài bảng, ≥ 0.
 */
export function pareto(report: TopReport): { rows: ParetoRow[]; otherVnd: number; otherCount: number } {
  let cum = 0;
  const rows = report.items.map((r) => {
    cum += r.billedVnd;
    const cumulativePct =
      report.grandTotalVnd > 0 ? Math.round((cum / report.grandTotalVnd) * 1000) / 10 : 0;
    return { ...r, cumulativePct };
  });
  const topCount = report.items.reduce((s, r) => s + r.count, 0);
  return {
    rows,
    otherVnd: Math.max(0, report.grandTotalVnd - cum),
    otherCount: Math.max(0, report.grandCount - topCount),
  };
}

interface RawTopServiceRow {
  serviceId: string;
  name: string;
  count: number;
  billedVnd: number;
}

interface RawTopMedicineRow {
  medicineId: string;
  name: string;
  count: number;
  billedVnd: number;
}

interface RawTopReport<T> {
  items: T[];
  grandTotalVnd: number;
  grandCount: number;
}

export interface BranchRevenueRow extends FinancialTotals {
  branchId: string | null;
  branchName: string;
  count: number;
}

export interface RetentionSummary {
  totalCustomers: number;
  repeatCustomers: number;
  totalVisits: number;
  rebookingRatePct: number;
  avgVisitsPerCustomer: number;
}

/** Chiều gom nhóm của `/reports/appointments/summary` (#19857). */
export type AppointmentGroupBy =
  | "day"
  | "week"
  | "month"
  | "status"
  | "channel"
  | "department"
  | "doctor"
  | "branch"
  | "hour";

/** Số lịch theo trạng thái. `total` = tổng sáu trạng thái. */
export interface AppointmentStatusCounts {
  total: number;
  requested: number;
  confirmed: number;
  checkedIn: number;
  completed: number;
  cancelled: number;
  noShow: number;
}

/**
 * Thống kê lịch hẹn (#19857 / STAT-20). Định nghĩa từng KPI ở `OperationsReportService`:
 * - `totals` (K-01): lịch có NGÀY KHÁM trong kỳ, không gồm lịch chuyển khoa.
 * - `createdInPeriod` (K-02): lịch TẠO trong kỳ, bất kể ngày khám.
 * - `attendanceRatePct` / `noShowRatePct` (K-03/K-04): chỉ ngày khám TRƯỚC hôm nay; `null` khi
 *   chưa có ngày nào để đo — khác với 0%.
 * - `cancelRatePct` (K-05): huỷ / K-01.
 * - `notClosed`: REQUESTED / CONFIRMED đã quá ngày — hiện riêng, không gộp vào vắng.
 * - `selfOnly`: số đã bị server buộc vào bác sĩ của người xem (`report.self.read`).
 */
export interface AppointmentSummary {
  fromDate: string;
  toDate: string;
  groupBy: AppointmentGroupBy;
  selfOnly: boolean;
  totals: AppointmentStatusCounts;
  createdInPeriod: number;
  attendanceBase: number;
  attendanceRatePct: number | null;
  noShowRatePct: number | null;
  cancelRatePct: number | null;
  notClosed: number;
  /** Bác sĩ mà số bị buộc vào khi `selfOnly` — để link danh sách cùng điều kiện với con số. */
  selfDoctorId: string | null;
  /**
   * Đang chờ khám (CHECKED_IN) — KỂ CẢ lịch chuyển khoa, khác `totals.checkedIn` (K-01 loại
   * chuyển khoa). Đây là số cho ô "Đang chờ khám" trên Trang chủ.
   */
  waitingNow: number;
  /** REQUESTED / CONFIRMED đã quá GIỜ hẹn, tính tới lúc gọi — cảnh báo "Quá giờ chưa tới". */
  lateNotArrived: number;
  /** Lịch còn hiệu lực chưa gán phòng (kể cả lịch chuyển khoa) — chưa hiện trên màn khám nào. */
  noRoom: number;
  byChannel: { channel: string; count: number }[];
  /** `key` null = chưa gán; `label` null = màn hình tự đặt nhãn. */
  groups: {
    key: string | null;
    label: string | null;
    counts: AppointmentStatusCounts;
    notClosed: number;
  }[];
  /**
   * Cùng các KPI của kỳ trước — cùng số ngày, liền trước, cùng phạm vi (#19864). Server bỏ trường
   * null khỏi JSON (NON_NULL), nên tỷ lệ có mẫu số 0 đến đây là VẮNG MẶT, không phải `null`.
   */
  previous?: {
    fromDate: string;
    toDate: string;
    total: number;
    createdInPeriod: number;
    attendanceBase: number;
    attendanceRatePct?: number | null;
    noShowRatePct?: number | null;
    cancelRatePct?: number | null;
  };
}

/** Chiều gom nhóm của `/reports/visits/summary` (#19858). */
export type VisitGroupBy = "day" | "doctor" | "department" | "room" | "service" | "hour";

/**
 * Một đại lượng thời gian, phút (K-12 chờ khám, K-13 thời lượng khám). Trung vị / P90 chỉ tính trên
 * `measured`; `abnormal` (âm hoặc > 8 giờ) bị loại; `missing` = không đủ dữ liệu (lịch trước khi
 * có giờ check-in).
 */
export interface MinuteStats {
  measured: number;
  abnormal: number;
  missing: number;
  medianMinutes: number | null;
  p90Minutes: number | null;
}

/**
 * Thống kê lượt khám (#19858 / STAT-21). "Lượt khám" = lịch COMPLETED (Q3), kể cả chuyển khoa.
 * - `uniquePatients` (K-08): hồ sơ đã gộp quy về hồ sơ gốc.
 * - `returningPatients` / `returningRatePct` (K-10/K-11): đã từng khám trước kỳ.
 * - `pendingCompletion`: đã đến, ngày khám đã qua, bệnh án chưa ký.
 * - `heatmap`: theo thứ (1 = thứ Hai … 7 = Chủ nhật) × giờ check-in; `heatmapUnknown` = lượt
 *   không có giờ check-in.
 */
export interface VisitSummary {
  fromDate: string;
  toDate: string;
  groupBy: VisitGroupBy;
  selfOnly: boolean;
  visits: number;
  uniquePatients: number;
  returningPatients: number;
  returningRatePct: number | null;
  pendingCompletion: number;
  waitTime: MinuteStats;
  duration: MinuteStats;
  groups: { key: string | null; label: string | null; visits: number; patients: number }[];
  heatmap: { isoDow: number; hour: number; visits: number }[];
  heatmapUnknown: number;
  /** Kỳ trước (#19864); `waitMedianMinutes` null = kỳ trước không có lượt nào đo được. */
  previous?: {
    fromDate: string;
    toDate: string;
    visits: number;
    uniquePatients: number;
    waitMedianMinutes?: number | null;
  };
}

/**
 * Nhóm tuổi và chất lượng hồ sơ (#19862 / STAT-24).
 * - `ageGroups`: bệnh nhân có lượt khám trong kỳ, theo tuổi TẠI NGÀY KHÁM ĐẦU TIÊN trong kỳ (hàm
 *   tính tuổi chung, tôn trọng độ chính xác ngày sinh). Nhóm `unknown` = hồ sơ không có ngày sinh.
 * - `completeness`: mức phòng khám, KHÔNG theo kỳ — hiện trạng hồ sơ còn hiệu lực.
 */
export interface PatientProfileSummary {
  fromDate: string;
  toDate: string;
  visitedPatients: number;
  ageGroups: { code: string; label: string; patients: number }[];
  completeness: {
    activePatients: number;
    missingPhone: number;
    missingDateOfBirth: number;
    missingNationalId: number;
    missingAddress: number;
  };
  pendingDuplicates: number;
}

/**
 * Ngưỡng cảnh báo vận hành của phòng khám (#19867 / STAT-34). Trường vắng mặt / `null` = chưa đặt =
 * cảnh báo đó TẮT — không có ngưỡng mặc định.
 */
export interface AlertSettings {
  waitMedianMinutes?: number | null;
  noShowRisePp?: number | null;
}

/**
 * Một cảnh báo đang bật, server tính. `metric` + `fromDate`/`toDate` mở đúng danh sách tạo nên con số
 * (drill-down); `metric` vắng mặt = không có danh sách (trung vị thời gian chờ).
 */
export interface ReportAlert {
  code: "PENDING_COMPLETION" | "WAIT_MEDIAN" | "NO_SHOW_RISE";
  value: number;
  threshold?: number | null;
  current?: number | null;
  baseline?: number | null;
  fromDate: string;
  toDate: string;
  metric?: string | null;
}

export interface ReportAlerts {
  today: string;
  selfOnly: boolean;
  settings: AlertSettings;
  alerts: ReportAlert[];
}

export const reportsApi = {
  alerts(): Promise<ReportAlerts> {
    return apiClient.get<ReportAlerts>("/v1/reports/alerts").then((r) => r.data);
  },
  alertSettings(): Promise<AlertSettings> {
    return apiClient.get<AlertSettings>("/v1/reports/alert-settings").then((r) => r.data);
  },
  updateAlertSettings(s: AlertSettings): Promise<AlertSettings> {
    return apiClient.put<AlertSettings>("/v1/reports/alert-settings", s).then((r) => r.data);
  },
  patientProfile(fromDate: string, toDate: string): Promise<PatientProfileSummary> {
    return apiClient
      .get<PatientProfileSummary>("/v1/reports/patients/profile", { params: { fromDate, toDate } })
      .then((r) => r.data);
  },
  visitsSummary(
    fromDate: string,
    toDate: string,
    groupBy: VisitGroupBy = "day"
  ): Promise<VisitSummary> {
    return apiClient
      .get<VisitSummary>("/v1/reports/visits/summary", {
        params: { fromDate, toDate, groupBy },
      })
      .then((r) => r.data);
  },
  appointmentsSummary(
    fromDate: string,
    toDate: string,
    groupBy: AppointmentGroupBy = "day"
  ): Promise<AppointmentSummary> {
    return apiClient
      .get<AppointmentSummary>("/v1/reports/appointments/summary", {
        params: { fromDate, toDate, groupBy },
      })
      .then((r) => r.data);
  },
  revenueDaily(fromDate?: string | null, toDate?: string | null) {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<RevenueDailyRow[]>("/v1/reports/revenue/daily", { params })
      .then((r) => r.data);
  },
  revenueMonthly(fromDate?: string | null, toDate?: string | null) {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<RevenueMonthlyRow[]>("/v1/reports/revenue/monthly", { params })
      .then((r) => r.data);
  },
  patientsSummary(fromDate?: string | null, toDate?: string | null) {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<PatientsSummary>("/v1/reports/patients", { params })
      .then((r) => r.data);
  },
  topServices(
    fromDate?: string | null,
    toDate?: string | null,
    limit = 10
  ): Promise<TopReport> {
    const params: Record<string, string | number> = { limit };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<RawTopReport<RawTopServiceRow>>("/v1/reports/top-services", { params })
      .then((r) => ({
        items: r.data.items.map((x) => ({
          id: x.serviceId,
          name: x.name,
          count: x.count,
          billedVnd: x.billedVnd,
        })),
        grandTotalVnd: r.data.grandTotalVnd,
        grandCount: r.data.grandCount,
      }));
  },
  topMedicines(
    fromDate?: string | null,
    toDate?: string | null,
    limit = 10
  ): Promise<TopReport> {
    const params: Record<string, string | number> = { limit };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<RawTopReport<RawTopMedicineRow>>("/v1/reports/top-medicines", { params })
      .then((r) => ({
        items: r.data.items.map((x) => ({
          id: x.medicineId,
          name: x.name,
          count: x.count,
          billedVnd: x.billedVnd,
        })),
        grandTotalVnd: r.data.grandTotalVnd,
        grandCount: r.data.grandCount,
      }));
  },
  revenueByBranch(
    fromDate?: string | null,
    toDate?: string | null
  ): Promise<BranchRevenueRow[]> {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<BranchRevenueRow[]>("/v1/reports/revenue/by-branch", { params })
      .then((r) => r.data);
  },
  retention(
    fromDate?: string | null,
    toDate?: string | null
  ): Promise<RetentionSummary> {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient
      .get<RetentionSummary>("/v1/reports/retention", { params })
      .then((r) => r.data);
  },
};

export const reportsQueryKeys = {
  alerts: () => ["reports", "alerts"] as const,
  alertSettings: () => ["reports", "alertSettings"] as const,
  patientProfile: (f: string, t: string) => ["reports", "patientProfile", f, t] as const,
  visitsSummary: (f: string, t: string, g: VisitGroupBy) =>
    ["reports", "visitsSummary", f, t, g] as const,
  appointmentsSummary: (f: string, t: string, g: AppointmentGroupBy) =>
    ["reports", "appointmentsSummary", f, t, g] as const,
  revenueDaily: (f?: string | null, t?: string | null) =>
    ["reports", "revenueDaily", f ?? null, t ?? null] as const,
  revenueMonthly: (f?: string | null, t?: string | null) =>
    ["reports", "revenueMonthly", f ?? null, t ?? null] as const,
  patients: (f?: string | null, t?: string | null) =>
    ["reports", "patients", f ?? null, t ?? null] as const,
  topServices: (f?: string | null, t?: string | null, limit?: number) =>
    ["reports", "topServices", f ?? null, t ?? null, limit ?? 10] as const,
  topMedicines: (f?: string | null, t?: string | null, limit?: number) =>
    ["reports", "topMedicines", f ?? null, t ?? null, limit ?? 10] as const,
  revenueByBranch: (f?: string | null, t?: string | null) =>
    ["reports", "revenueByBranch", f ?? null, t ?? null] as const,
  retention: (f?: string | null, t?: string | null) =>
    ["reports", "retention", f ?? null, t ?? null] as const,
};

/** Format VND integer with thousand separator. */
export function vnd(n: number): string {
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

/**
 * Trung hoà một ô CSV chống CSV/formula injection (#19852).
 *
 * Tên dịch vụ, tên thuốc, tên chi nhánh là chữ người dùng tự nhập. Một ô bắt đầu bằng `=`, `+`,
 * `-`, `@`, tab hoặc CR sẽ được Excel coi là CÔNG THỨC khi mở file. Thêm tiền tố `'` biến nó thành
 * chữ. Chỉ áp cho ô CHUỖI: ô SỐ (kể cả số âm) giữ nguyên để Excel còn cộng trừ được.
 */
export function csvCell(v: string | number): string {
  if (typeof v === "number") return String(v);
  return /^[=+\-@\t\r]/.test(v) ? "'" + v : v;
}

/**
 * Tên tệp xuất của một báo cáo TỔNG HỢP (#19865 / STAT-32): tên báo cáo, khoảng ngày và phạm vi chi
 * nhánh — hai tệp "doanh-thu-ngay-…csv" của hai chi nhánh khác nhau không còn trùng tên, và một
 * tệp mở ra sau này vẫn tự nói nó là số của chi nhánh nào. `scopeLabel` là nhãn "Đang xem" của
 * `useBranchScope`; bỏ dấu tiếng Việt để tên tệp dùng được ở mọi hệ điều hành.
 */
export function reportFileName(base: string, from: string, to: string, scopeLabel: string): string {
  const slug = scopeLabel
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${base}_${from}_${to}${slug ? `_${slug}` : ""}.csv`;
}

/**
 * Tải một tệp xuất PHÍA SERVER (#19865 mục 2) — danh sách mang PHI không dựng ở trình duyệt từ các
 * dòng đang hiển thị: server áp cùng quyền, cùng bộ lọc, giới hạn số dòng và ghi dòng kiểm toán. Tên
 * tệp lấy từ `Content-Disposition` của server. Lỗi (ví dụ 400 `export_too_large`) ném ra với câu
 * `detail` của server, vì thân lỗi về dưới dạng Blob chứ không phải JSON.
 */
/** `Blob.text()` khi có; `FileReader` khi không (trình duyệt cũ, jsdom). */
function blobText(b: Blob): Promise<string> {
  if (typeof b.text === "function") return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

export async function downloadServerCsv(
  path: string,
  params: Record<string, string | number | undefined>,
  fallbackName: string
): Promise<void> {
  let res;
  try {
    res = await apiClient.get(path, { params, responseType: "blob" });
  } catch (e) {
    const data = (e as { response?: { data?: unknown } }).response?.data;
    let message = "Không xuất được tệp. Hãy thử lại.";
    if (data instanceof Blob) {
      try {
        const body = JSON.parse(await blobText(data)) as { detail?: string };
        if (body.detail) message = body.detail;
      } catch {
        // thân lỗi không phải JSON — giữ câu chung
      }
    }
    throw new Error(message);
  }
  const header = String(res.headers?.["content-disposition"] ?? "");
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  const name = star ? decodeURIComponent(star[1]) : plain ? plain[1] : fallbackName;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Trigger a client-side CSV download. */
export function downloadCsv(filename: string, rows: (string | number)[][]) {
  const escape = (v: string | number) => {
    const s = csvCell(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // BOM UTF-8 ở đầu (#19817): thiếu nó, Excel trên Windows đọc file theo bảng mã ANSI và tiêu đề
  // tiếng Việt vỡ font. Google Sheets / LibreOffice bỏ qua BOM nên không ảnh hưởng.
  const body = "﻿" + rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Nhãn kênh đặt lịch (#19855). `UNKNOWN` là dữ liệu TRƯỚC ngày tách kênh — màn hình phải nói đúng
 * điều đó, không gọi nó là "khác".
 */
export const appointmentChannelLabel: Record<string, string> = {
  STAFF: "Nhân viên đặt",
  WEBSITE: "Website",
  ZALO_MINIAPP: "Zalo Mini App",
  AI_ASSISTANT: "Trợ lý AI",
  EXTERNAL_SLIP: "Phiếu khám ngoài",
  UNKNOWN: "Không xác định",
};
